// homeCommunity/view.js
import React, { useState, useRef } from 'react';
import { Tab, useMediaQuery, Button, Box } from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import BusinessIcon from '@mui/icons-material/Business';
import EventIcon from '@mui/icons-material/Event';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router';
import CommunityProfile from 'components/pages/community/communityProfile';
import { API_BASE_URL } from 'config/api';

// Import styled components
import {
    StyledTabs,
    MainContainer,
    MobileSidebarToggle,
    MainContent,
    HeaderContainer,
    BackgroundImage,
    ContentOverlay,
    LogoContainer,
    LogoBox,
    LogoImage,
    LogoPlaceholder,
    CommunityInfoContainer,
    CommunityTitle,
    CommunityDescription,
    StatsCardsContainer,
    StatCard,
    StatNumber,
    StatLabel,
    ActionButtonsContainer,
    ManageButton,
    MainContentArea,
    TabContentContainer,
    OverviewTabContainer,
    TopJoinButton,
    StatsBar,
    StatsBarItem,
    StatsBarNumber,
    StatsBarLabel,
} from './styled';

// Import sub-components
import CommunitySidebar from '../communitySidebar';
import MemberChatWindow from '../memberChatWindow';
import MemberDetail from '../memberDetail';
import { JoinCommunityFab, ShareCommunityButton } from '../communityActions';
import RecentMembersSection from '../recentMembersSection';
import MembersTab from '../membersTab';
import PartnersSection from '../partnersSection';
import WelcomeBanner from '../welcomeBanner';
import UpcomingEventsSection from '../upcomingEventsSection';
import AboutCommunity from '../aboutCommunity';
import JoinBookingDialog from 'components/molecules/joinBookingDialog';

// Import image
import coworkingBg from 'assets/images/coworking.jpg';

const HomeCommunityView = ({
    // Data props
    community,
    members,
    activeMembers,
    events,
    communityBookings,
    messages,
    venues,
    stats,
    my_membership,
    
    // State props
    tabValue,
    loading,
    selectedDate,
    sidebarOpen,
    sidebarCollapsed,
    activeChatMember,
    selectedMemberDetail,
    isAdmin,
    isAuthenticated,
    
    // Join booking state
    joinBookingDialogOpen,
    selectedBookingToJoin,
    joinBookingLoading,
    
    // Handler props
    onTabChange,
    onDateSelect,
    onOpenChat,
    onCloseChat,
    onOpenMemberDetail,
    onCloseMemberDetail,
    onToggleSidebarCollapse,
    onViewAllAnnouncements,
    onCreateAnnouncement,
    onViewAllEvents,
    onCreateEvent,
    onViewAllMembers,
    onCreateMessage,
    onCreateEventAction,
    onInviteMember,
    onJoinRequest,
    onSidebarToggle,
    selectedMessageId,
    onOpenMessage,
    onEventsUpdate,
    onMembershipUpdate,
    
    // Join booking handlers
    onJoinBooking,
    onCloseJoinBookingDialog,
    onConfirmJoinBooking,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Helper function to get proper image URL
    const getImageUrl = (image) => {
        if (!image) return null;
        if (typeof image === 'object') {
            return URL.createObjectURL(image);
        }
        return image.startsWith('/') ? `${API_BASE_URL}${image}` : image;
    };

    const handleShareCommunity = () => {
        setTimeout(() => {
            const shareButton = document.querySelector('button[aria-label*="Share"]') || 
                               document.querySelector('button:has([data-testid="ShareIcon"])') ||
                               document.querySelector('button span:contains("Share")').closest('button');
            if (shareButton) {
                shareButton.click();
            }
        }, 50);
    };

    return (
        <MainContainer>
            {/* Main Content */}
            <MainContent component="main">
                {/* Background Header with Overlapping Cards */}
                <HeaderContainer>
                    {/* Background Image */}
                    <BackgroundImage backgroundImage={getImageUrl(community?.background) || coworkingBg} />

                    {/* Content Overlay */}
                    <ContentOverlay>
                        {/* Top Row: Logo Only */}
                        <LogoContainer>
                            <LogoBox>
                                {community?.image ? (
                                    <LogoImage
                                        src={getImageUrl(community.image)}
                                        alt={community.name}
                                    />
                                ) : (
                                    <LogoPlaceholder>
                                        {community?.name?.charAt(0) || 'C'}
                                    </LogoPlaceholder>
                                )}
                            </LogoBox>
                        </LogoContainer>

                        {/* Bottom Row: Community Info */}
                        <CommunityInfoContainer>
                            <CommunityTitle
                                variant="h3"
                                fontWeight={800}
                                gutterBottom
                            >
                                {community?.name || 'T-Break Club'}
                            </CommunityTitle>

                            <CommunityDescription variant="body1">
                                {community?.description || 'Welcome to our tech community, where London\'s tech enthusiasts gather to share knowledge and enhance their freelance and startup experiences.'}
                            </CommunityDescription>
                        </CommunityInfoContainer>
                    </ContentOverlay>
                </HeaderContainer>

                {/* Main Content Area */}
                <MainContentArea>
                    {/* Stats Bar */}
                    <StatsBar>
                        <StatsBarItem>
                            <PeopleIcon sx={{ color: '#F1C40F', fontSize: '1.3rem', flexShrink: 0 }} />
                            <Box>
                                <StatsBarNumber>{members?.length ?? 0}</StatsBarNumber>
                                <StatsBarLabel>Members</StatsBarLabel>
                            </Box>
                        </StatsBarItem>
                        <StatsBarItem>
                            <BusinessIcon sx={{ color: '#F1C40F', fontSize: '1.3rem', flexShrink: 0 }} />
                            <Box>
                                <StatsBarNumber>{venues?.length ?? 0}</StatsBarNumber>
                                <StatsBarLabel>Venues</StatsBarLabel>
                            </Box>
                        </StatsBarItem>
                        <StatsBarItem>
                            <EventIcon sx={{ color: '#F1C40F', fontSize: '1.3rem', flexShrink: 0 }} />
                            <Box>
                                <StatsBarNumber>{events?.length ?? 0}</StatsBarNumber>
                                <StatsBarLabel>Events</StatsBarLabel>
                            </Box>
                        </StatsBarItem>
                        {community?.city && (
                            <StatsBarItem>
                                <LocationOnIcon sx={{ color: '#F1C40F', fontSize: '1.3rem', flexShrink: 0 }} />
                                <Box>
                                    <StatsBarNumber>{community.city}</StatsBarNumber>
                                    <StatsBarLabel>Location</StatsBarLabel>
                                </Box>
                            </StatsBarItem>
                        )}
                    </StatsBar>

                    {/* Tabs with Top Action Buttons */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <StyledTabs
                            value={tabValue}
                            onChange={onTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                            style={{ flex: 1 }}
                        >
                            <Tab label="Overview" />
                            <Tab label="Members" />
                            {isAdmin && <Tab label="Manage" />}
                            {my_membership && <Tab label="Your Profile" />}
                        </StyledTabs>

                        {/* Top Action Buttons */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '16px' }}>
                            {/* Join Community Button - with data attribute for WelcomeBanner trigger */}
                            <div data-join-button>
                                <JoinCommunityFab
                                    community={community}
                                    onJoinRequest={onJoinRequest}
                                    my_membership={my_membership}
                                />
                            </div>

                            {/* Share Community Button */}
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleShareCommunity}
                                sx={{ 
                                    borderRadius: '16px',
                                    textTransform: 'none',
                                    minWidth: '80px',
                                    height: '32px',
                                    padding: '4px 12px',
                                    fontWeight: 600,
                                    fontSize: '0.75rem',
                                    letterSpacing: '0.3px',
                                    border: '2px solid rgba(241, 196, 15, 0.3)',
                                    color: '#F1C40F',
                                    '&:hover': {
                                        backgroundColor: 'rgba(241, 196, 15, 0.1)',
                                        border: '2px solid rgba(241, 196, 15, 0.5)',
                                        transform: 'translateY(-1px) scale(1.02)',
                                    },
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                }}
                            >
                                Share
                            </Button>
                        </div>
                    </div>

                    {/* Tab Content */}
                    <TabContentContainer>
                        {/* Overview Tab */}
                        {tabValue === 0 && (
                            <OverviewTabContainer>
                                {/* Welcome Banner for non-members */}
                                {!my_membership && (
                                    <WelcomeBanner
                                        community={community}
                                        venues={venues}
                                        members={members}
                                        upcomingEvents={events}
                                        onJoinClick={() => {
                                            // Trigger the join dialog by clicking the hidden JoinCommunityFab
                                            const joinButton = document.querySelector('[data-join-button]');
                                            if (joinButton) joinButton.click();
                                        }}
                                    />
                                )}

                                {/* About Community - Full for non-members, collapsed for members */}
                                <AboutCommunity
                                    community={community}
                                    members={members}
                                    venues={venues}
                                    upcomingEvents={events}
                                    isMember={!!my_membership}
                                />

                                <PartnersSection
                                    venues={venues}
                                    communitySlug={community?.slug}
                                    onVenueClick={() => {}}
                                />

                                <UpcomingEventsSection
                                    events={events}
                                    communityBookings={communityBookings}
                                    onEventClick={(event) => {
                                        // Navigate to event detail or handle event click
                                        console.log('Event clicked:', event);
                                    }}
                                    onBookingClick={(booking) => {
                                        // Navigate to venue page with preselected date, booking type, and community
                                        if (booking.venue && booking.venue.slug) {
                                            const params = new URLSearchParams({
                                                date: booking.planned_start_day,
                                                type: booking.booking_type,
                                                community: community?.slug || '',
                                            });
                                            // Add booking_plan_code if available for joining flow
                                            if (booking.booking_plan_code) {
                                                params.set('booking_plan', booking.booking_plan_code);
                                            }
                                            navigate(`/venue/${booking.venue.slug}?${params.toString()}`);
                                        }
                                    }}
                                    onJoinBooking={onJoinBooking}
                                    onQuickBook={(booking) => {
                                        // Navigate to venue page for quick booking
                                        if (booking.venue && booking.venue.slug) {
                                            const params = new URLSearchParams({
                                                date: booking.planned_start_day,
                                                type: booking.booking_type,
                                                community: community?.slug || '',
                                            });
                                            navigate(`/venue/${booking.venue.slug}?${params.toString()}`);
                                        }
                                    }}
                                    onViewAll={onViewAllEvents}
                                    isAuthenticated={isAuthenticated}
                                />
                                
                                <RecentMembersSection
                                    members={members}
                                    activeMembers={activeMembers}
                                    isAdmin={isAdmin}
                                    onMemberClick={onOpenMemberDetail}
                                    onViewAll={onViewAllMembers}
                                />
                            </OverviewTabContainer>
                        )}

                        {/* Members Tab */}
                        {tabValue === 1 && (
                            <MembersTab
                                community={community}   
                                members={members}
                                isAdmin={isAdmin}
                                onMemberClick={onOpenMemberDetail}
                                onInviteMember={onInviteMember}
                                onMembershipUpdate={onMembershipUpdate}
                            />
                        )}

                        {/* Manage Tab - Navigate to full management page */}
                        {tabValue === 2 && isAdmin && (
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                padding: 4,
                                gap: 2 
                            }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    onClick={() => navigate(`/community/${community?.slug}/manage`)}
                                    sx={{
                                        backgroundColor: '#F1C40F',
                                        color: '#000',
                                        fontWeight: 700,
                                        borderRadius: '12px',
                                        padding: '12px 32px',
                                        '&:hover': {
                                            backgroundColor: '#D4AC0D',
                                        }
                                    }}
                                >
                                    Open Community Management
                                </Button>
                            </Box>
                        )}

                        {/* Profile Tab - Available to all members */}
                        {tabValue === (isAdmin ? 3 : 2) && my_membership && (
                            <CommunityProfile
                                slug={community?.slug}
                                membershipSlug={my_membership?.slug}
                            />
                        )}
                    </TabContentContainer>
                </MainContentArea>
            </MainContent>

            {/* Hidden ShareCommunityButton for functionality */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
                <ShareCommunityButton community={community} />
            </div>

            {/* Chat Window */}
            {activeChatMember && (
                <MemberChatWindow
                    member={activeChatMember}
                    onClose={onCloseChat}
                />
            )}

            {/* Member Detail Modal - Used in Overview tab */}
            {selectedMemberDetail && (
                <MemberDetail
                    member={selectedMemberDetail}
                    open={Boolean(selectedMemberDetail)}
                    onClose={onCloseMemberDetail}
                    isAdmin={isAdmin}
                    onMemberUpdate={onMembershipUpdate} // ✅ ADD THIS!
                />
            )}

            {/* Join Booking Dialog */}
            <JoinBookingDialog
                open={joinBookingDialogOpen}
                onClose={onCloseJoinBookingDialog}
                booking={selectedBookingToJoin}
                onConfirm={onConfirmJoinBooking}
                loading={joinBookingLoading}
            />
        </MainContainer>
    );
};

export default HomeCommunityView;